import React from 'react';
import { CoachAvailabilityInterval, DayOfWeek } from 'globalTypes';
import {
  findValidTimeSelectOptions,
  consolidateTimeIntervals,
  firstTimeSelectOption,
  lastTimeSelectOption,
} from 'coaching/lib/timeIntervals';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import EventTracking from 'lib/EventTracking';

type Props = {
  dayOfWeek: DayOfWeek;
  initialIntervals?: CoachAvailabilityInterval[];
  onAvailabilityChange: (intervals: CoachAvailabilityInterval[], day: DayOfWeek) => void;
};

type State = {
  intervals: CoachAvailabilityInterval[];
};

export default class CoachingAvailabilityEditDayRow extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      intervals: props.initialIntervals ?? [],
    };
  }

  handleIntervalChanges = () => {
    const mergedIntervals = consolidateTimeIntervals(this.state.intervals);
    this.props.onAvailabilityChange(mergedIntervals, this.props.dayOfWeek);
  };

  addInterval = (interval: CoachAvailabilityInterval, index: number) => {
    const { intervals } = this.state;

    intervals.splice(index, 0, interval);
    this.setState({ intervals }, this.handleIntervalChanges);
  };

  removeInterval = (index: number, callback?: () => void) => {
    const { intervals } = this.state;
    intervals.splice(index, 1);
    this.setState({ intervals }, () => {
      if (callback) {
        callback();
      }
      this.handleIntervalChanges();
    });
  };

  updateIntervalStartTime = (newStartTime: string, endTime: string, index: number) => {
    this.removeInterval(index, () =>
      this.addInterval(
        {
          startTime: newStartTime,
          endTime,
        },
        index
      )
    );
  };

  updateIntervalEndTime = (startTime: string, newEndTime: string, index: number) => {
    this.removeInterval(index, () =>
      this.addInterval(
        {
          startTime,
          endTime: newEndTime,
        },
        index
      )
    );
  };

  renderTimeInputRow = (interval: CoachAvailabilityInterval, index: number) => {
    const { validStartTimeOptions, validEndTimeOptions } = findValidTimeSelectOptions(interval);

    return (
      <Row data-test="time-input-row" size="xsmall" key={`${interval.startTime}-${index}`}>
        <table className="CoachingIsolatedView-timePicker">
          <tbody>
            <tr>
              <td>
                <Select
                  searchable={false}
                  inline
                  full
                  options={validStartTimeOptions}
                  value={interval.startTime}
                  onChange={(newStartTime: string) => {
                    this.updateIntervalStartTime(newStartTime, interval.endTime, index);
                    EventTracking.trackKey(
                      'coaching.set_coach_availability.select_start_time.click'
                    );
                  }}
                />
              </td>
              <td className="CoachingIsolatedView-timePicker-text">
                <Text align="center" element="div">
                  to
                </Text>
              </td>
              <td>
                <Select
                  searchable={false}
                  inline
                  full
                  options={validEndTimeOptions}
                  value={interval.endTime}
                  onChange={(newEndTime: string) => {
                    this.updateIntervalEndTime(interval.startTime, newEndTime, index);
                    EventTracking.trackKey('coaching.set_coach_availability.select_end_time.click');
                  }}
                />
              </td>
              <td
                className="CoachingIsolatedView-timePicker-close"
                onClick={() => {
                  this.removeInterval(index);
                  EventTracking.trackKey('coaching.set_coach_availability.remove_hours.click');
                }}
                data-test="remove-interval-button">
                <Text align="right" element="div">
                  <Icon name="x" size="small" color="pitch" />
                </Text>
              </td>
            </tr>
          </tbody>
        </table>
      </Row>
    );
  };

  render() {
    const defaultInterval = {
      startTime: firstTimeSelectOption.value,
      endTime: lastTimeSelectOption.value,
    };
    const { intervals } = this.state;

    return (
      <React.Fragment>
        <Row size="small">
          <MediaObject center figure="right">
            <Text size="medium" weight="semibold">
              {this.props.dayOfWeek}
            </Text>
            <Link
              data-test="add-hours-action"
              weight="semibold"
              onClick={() => {
                this.addInterval(defaultInterval, intervals.length);
                EventTracking.trackKey('coaching.set_coach_availability.add_hours.click');
              }}>
              Add hours
            </Link>
          </MediaObject>
        </Row>
        {intervals.map((interval, index) => this.renderTimeInputRow(interval, index))}
        <Divider size="medium" />
      </React.Fragment>
    );
  }
}
